import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useProducts } from '@/hooks/use-products';
import { healthTextClass } from '@/lib/format';

const NAV_ITEMS = [
  {
    label: 'Ecosystem',
    path: '/',
    icon: (
      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
        <rect x="1.5" y="1.5" width="5" height="5" rx="1" />
        <rect x="9.5" y="1.5" width="5" height="5" rx="1" />
        <rect x="1.5" y="9.5" width="5" height="5" rx="1" />
        <rect x="9.5" y="9.5" width="5" height="5" rx="1" />
      </svg>
    ),
  },
  {
    label: 'DORA Metrics',
    path: '/dora',
    icon: (
      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="1.5 12.5 5 7 8.5 9.5 14.5 3" />
        <polyline points="10 3 14.5 3 14.5 7.5" />
      </svg>
    ),
  },
  {
    label: 'Security',
    path: '/security',
    icon: (
      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M8 1.5L2 4v4c0 3.5 2.5 6.2 6 7 3.5-.8 6-3.5 6-7V4L8 1.5z" />
        <polyline points="5.5 8 7.2 9.8 10.5 6.2" />
      </svg>
    ),
  },
];

function statusDotColor(score: number | null): string {
  if (score === null || score === 0) return 'bg-text-muted';
  if (score >= 85) return 'bg-success';
  if (score >= 70) return 'bg-warning';
  return 'bg-danger';
}

export function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const { data: products } = useProducts();

  const sidebarW = collapsed ? 'w-[60px]' : 'w-60';

  return (
    <aside
      className={`${sidebarW} fixed top-0 left-0 bottom-0 bg-bg-surface border-r border-border flex flex-col z-[100] transition-[width] duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] overflow-hidden`}
      role="navigation"
      aria-label="Main navigation"
    >
      {/* Header */}
      <div className={`px-3 py-4 flex items-center gap-2.5 border-b border-border ${collapsed ? 'flex-col justify-center' : ''}`}>
        <div className="w-8 h-8 rounded-md bg-[#007A7A] flex items-center justify-center font-display font-bold text-[0.72rem] text-white shrink-0">
          VD
        </div>
        {!collapsed && (
          <div className="flex flex-col overflow-hidden">
            <span className="font-display font-bold text-[0.92rem] tracking-tight leading-tight">VyraDash</span>
            <span className="text-[0.56rem] font-medium text-text-muted uppercase tracking-widest">Engineering Quality</span>
          </div>
        )}
        <button
          onClick={() => setCollapsed(c => !c)}
          className={`w-7 h-7 rounded-sm bg-transparent border border-transparent text-text-muted flex items-center justify-center hover:bg-bg-elevated hover:text-text-secondary hover:border-border transition-all shrink-0 ${collapsed ? 'mt-2 ml-0' : 'ml-auto'}`}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <svg
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={`w-3.5 h-3.5 transition-transform duration-200 ${collapsed ? 'rotate-180' : ''}`}
          >
            <path d="M10 3L5 8l5 5" />
          </svg>
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden py-2">
        {/* Dashboard section */}
        <div className="px-2 pt-2 pb-1">
          {!collapsed && (
            <div className="text-[0.56rem] font-semibold text-text-muted uppercase tracking-[0.1em] px-2 mb-1.5">
              Dashboard
            </div>
          )}
          <nav className="flex flex-col gap-px">
            {NAV_ITEMS.map(item => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/'}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-2.5 py-1.5 rounded-sm text-[0.8rem] font-medium transition-all min-h-[34px] border border-transparent ${
                    isActive
                      ? 'bg-[rgba(0,144,144,0.12)] text-accent-bright border-[rgba(0,144,144,0.25)]'
                      : 'text-text-secondary hover:bg-bg-elevated hover:text-text-primary'
                  } ${collapsed ? 'justify-center px-2' : ''}`
                }
                title={collapsed ? item.label : undefined}
              >
                <span className={`w-5 h-5 flex items-center justify-center shrink-0 [&_svg]:w-4 [&_svg]:h-4`}>
                  {item.icon}
                </span>
                {!collapsed && <span>{item.label}</span>}
              </NavLink>
            ))}
          </nav>
        </div>

        {/* Products section */}
        <div className="px-2 pt-2 pb-1">
          {!collapsed && (
            <div className="text-[0.56rem] font-semibold text-text-muted uppercase tracking-[0.1em] px-2 mb-1.5">
              Products
            </div>
          )}
          <div className="flex flex-col gap-px px-2">
            {products?.map(p => {
              const isActive = location.pathname === `/products/${p.slug}`;
              const displayScore = p.health_score !== null ? p.health_score : '—';
              return (
                <NavLink
                  key={p.slug}
                  to={`/products/${p.slug}`}
                  className={`flex items-center gap-2 py-1.5 px-2.5 rounded-sm text-[0.76rem] transition-all min-h-[32px] ${
                    isActive
                      ? 'bg-[rgba(0,144,144,0.12)] text-accent-bright'
                      : 'text-text-secondary hover:bg-bg-elevated hover:text-text-primary'
                  } ${collapsed ? 'justify-center px-1' : ''}`}
                  title={collapsed ? `${p.name} — ${displayScore}` : undefined}
                >
                  <span className={`w-2 h-2 rounded-full shrink-0 ${statusDotColor(p.health_score)}`} />
                  {!collapsed && (
                    <>
                      <span className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap">{p.name}</span>
                      <span className={`ml-auto font-mono text-[0.65rem] font-medium shrink-0 ${healthTextClass(p.health_score)}`}>
                        {displayScore}
                      </span>
                    </>
                  )}
                </NavLink>
              );
            })}
          </div>
        </div>
      </div>

      {/* Bottom */}
      <div className="mt-auto border-t border-border p-2 flex flex-col gap-px">
        <NavLink
          to="/settings"
          className={({ isActive }) =>
            `flex items-center gap-2.5 px-2.5 py-1.5 rounded-sm text-[0.8rem] font-medium transition-all min-h-[34px] border border-transparent ${
              isActive
                ? 'bg-[rgba(0,144,144,0.12)] text-accent-bright border-[rgba(0,144,144,0.25)]'
                : 'text-text-secondary hover:bg-bg-elevated hover:text-text-primary'
            } ${collapsed ? 'justify-center px-2' : ''}`
          }
          title={collapsed ? 'Settings' : undefined}
        >
          <span className="w-5 h-5 flex items-center justify-center shrink-0">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
              <path d="M6.6 1.5h2.8l.4 2.1a5 5 0 011.3.7l2-.8 1.4 2.4-1.6 1.4a5 5 0 010 1.4l1.6 1.4-1.4 2.4-2-.8a5 5 0 01-1.3.7l-.4 2.1H6.6l-.4-2.1a5 5 0 01-1.3-.7l-2 .8-1.4-2.4 1.6-1.4a5 5 0 010-1.4L1.5 5.9l1.4-2.4 2 .8a5 5 0 011.3-.7l.4-2.1z" />
              <circle cx="8" cy="8" r="2" />
            </svg>
          </span>
          {!collapsed && <span>Settings</span>}
        </NavLink>

        {/* User footer */}
        <div className={`px-2.5 py-2 flex items-center gap-2 ${collapsed ? 'justify-center' : ''}`}>
          <div className="w-7 h-7 rounded-full bg-[#007A7A] flex items-center justify-center text-[0.62rem] font-bold text-white shrink-0">
            KK
          </div>
          {!collapsed && (
            <div className="flex-1 overflow-hidden">
              <div className="text-[0.76rem] font-medium text-text-secondary whitespace-nowrap overflow-hidden text-ellipsis">
                Kyle Kendrick
              </div>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}
